import { Button, Card, Stack, Typography } from '@mui/joy';
import QuestionBubbleIcon from 'jsx:../../assets/icons/question-square-bubble.svg';

import { usePermissionsContext } from '../../context/permissionsContext';
import clearfeed from '../../services/support/clearfeed';

import { DEFAULT_CTA_LABEL, DEFAULT_SUBTITLE, DEFAULT_TITLE, VARIANTS } from './constants';
import type { AskAQuestionCardProps } from './types';

const AskAQuestionCard: React.FC<AskAQuestionCardProps> = ({
  prefill,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  ctaLabel = DEFAULT_CTA_LABEL,
  width,
  variant = 'default',
  ctaVariant,
}) => {
  // Per the Figma note on frame 4:2 — question prompts are paid-only.
  const { isFreemiumUser } = usePermissionsContext();
  if (isFreemiumUser) return null;

  const style = VARIANTS[variant];

  // Figma node 1837:251260 (Questions card, project shell — outlined variant):
  //   card r=8, padding 16, gap 16
  //   title  : 16/700 lh=21.28 #171A1C
  //   label  : 12/500 lh=15.96 #555E68
  //   button : 14/400 lh=14    #4D4D4D, border #056B0A 2px, r=8, h=32, px=12, gap=4
  const isOutlined = variant === 'outlined';
  const isStacked = variant === 'stacked';
  const buttonVariant = ctaVariant ?? style.button.variant;

  return (
    <Card
      variant={style.container.variant}
      sx={{
        p: style.container.padding,
        gap: style.outerGap,
        flexDirection: style.container.direction ?? 'row',
        alignItems: isStacked ? 'flex-start' : 'center',
        borderRadius: (theme) => (isOutlined ? '8px' : theme.radius.sm),
        ...(style.container.variant === 'plain' && {
          background: 'transparent',
          boxShadow: 'none',
        }),
        ...(width !== undefined ? { width, maxWidth: '100%', flexShrink: 0 } : { width: '100%' }),
      }}
    >
      {style.icon.show && (
        <QuestionBubbleIcon
          width={style.icon.size}
          height={style.icon.size}
          style={{ flexShrink: 0 }}
        />
      )}
      <Stack
        direction={style.text.direction}
        alignItems={style.text.alignItems}
        gap={style.text.innerGap}
        flex={isStacked ? undefined : 1}
      >
        <Typography
          level={style.title.level}
          fontWeight={'xl'}
          sx={{
            color: (theme) => theme.palette.text.primary,
            ...(isOutlined && { fontSize: '16px', lineHeight: '21.28px' }),
            ...(isStacked && { fontSize: '16px', lineHeight: 1.33 }),
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            fontSize={style.subtitle.fontSize}
            fontWeight={style.subtitle.fontWeight}
            lineHeight={isOutlined ? '15.96px' : isStacked ? 1.33 : 1.42}
            noWrap={style.subtitle.noWrap}
            sx={{ color: (theme) => theme.palette.text.tertiary }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Button
        variant={buttonVariant}
        color={buttonVariant === 'solid' ? 'primary' : style.button.color}
        size='sm'
        onClick={() => clearfeed.open(prefill)}
        sx={{
          textWrap: 'nowrap',
          height: 32,
          borderRadius: (theme) => (isOutlined ? '8px' : theme.radius.sm),
          fontWeight: isOutlined ? 400 : 'md',
          ...(isOutlined && {
            fontSize: '14px',
            lineHeight: '14px',
            px: '12px',
            gap: '4px',
          }),
          ...(isStacked && {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '14px',
            px: '12px',
            borderRadius: '8px',
          }),
        }}
      >
        {ctaLabel}
      </Button>
    </Card>
  );
};

export { AskAQuestionCard };
export type { AskAQuestionVariant } from './types';
